import React, { useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
  Tooltip,
} from '@mui/material';
import {
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Help as HelpIcon,
  ContentCopy as ContentCopyIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { FinancialError, FinancialErrorHandler } from '../../utils/financial-error-handling';

interface FinancialErrorDisplayProps {
  error: FinancialError;
  onRetry?: () => void;
  onDismiss?: () => void;
  showDetails?: boolean;
  compact?: boolean;
  className?: string;
}

const FinancialErrorDisplay: React.FC<FinancialErrorDisplayProps> = ({
  error,
  onRetry,
  onDismiss,
  showDetails = false,
  compact = false,
  className,
}) => {
  const [expanded, setExpanded] = useState(showDetails);
  const [copied, setCopied] = useState(false);

  const displayInfo = FinancialErrorHandler.formatForDisplay(error);

  const getSeverityIcon = () => {
    switch (displayInfo.severity) {
      case 'error':
        return <ErrorIcon color="error" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      case 'info':
        return <InfoIcon color="info" />;
      default:
        return <ErrorIcon color="error" />;
    }
  };

  const getSeverityColor = (): 'error' | 'warning' | 'info' => {
    return displayInfo.severity === 'info' ? 'info' : 
           displayInfo.severity === 'warning' ? 'warning' : 'error';
  };

  const handleCopyError = async () => {
    const errorDetails = {
      type: error.type,
      message: error.message,
      details: error.details,
      context: error.context,
      timestamp: new Date().toISOString(),
    };

    try {
      await navigator.clipboard.writeText(JSON.stringify(errorDetails, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy error details:', err);
    }
  };

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    }
  };

  const handleHelpClick = () => {
    if (displayInfo.actionUrl) {
      window.open(displayInfo.actionUrl, '_blank');
    }
  };

  if (compact) {
    return (
      <Alert 
        severity={getSeverityColor()} 
        className={className}
        action={
          <Stack direction="row" spacing={1}>
            {error.retryable && onRetry && (
              <Button size="small" onClick={handleRetry}>
                Retry
              </Button>
            )}
            {onDismiss && (
              <Button size="small" onClick={onDismiss}>
                Dismiss
              </Button>
            )}
          </Stack>
        }
      >
        <AlertTitle>{displayInfo.title}</AlertTitle>
        {error.userMessage || error.message}
      </Alert>
    );
  }

  return (
    <Paper elevation={2} className={className} sx={{ p: 2 }}>
      <Stack spacing={2}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          {getSeverityIcon()}
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" gutterBottom>
              {displayInfo.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {error.userMessage || error.message}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title={copied ? 'Copied!' : 'Copy error details'}>
              <IconButton size="small" onClick={handleCopyError}>
                {copied ? <CheckCircleIcon color="success" /> : <ContentCopyIcon />}
              </IconButton>
            </Tooltip>
            {error.details && (
              <IconButton size="small" onClick={() => setExpanded(!expanded)}>
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            )}
          </Stack>
        </Box>

        {/* Error metadata */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip 
            label={error.severity?.toUpperCase() || 'UNKNOWN'} 
            color={getSeverityColor()} 
            size="small" 
          />
          {error.retryable && (
            <Chip label="RETRYABLE" color="success" size="small" />
          )}
          {error.retryAfter && (
            <Chip 
              label={`RETRY IN ${Math.ceil(error.retryAfter / 60)}M`} 
              color="info" 
              size="small" 
            />
          )}
        </Box>

        {/* Suggested solutions */}
        {error.suggestedAction && (
          <Alert severity="info" variant="outlined">
            <AlertTitle>Suggested Action</AlertTitle>
            {error.suggestedAction}
          </Alert>
        )}

        {/* Solutions list */}
        <Box>
          <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HelpIcon color="primary" fontSize="small" />
            Possible Solutions
          </Typography>
          <List dense>
            {FinancialErrorHandler.getSuggestedSolutions(error).map((solution, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 24 }}>
                  <Typography variant="body2" color="primary">•</Typography>
                </ListItemIcon>
                <ListItemText 
                  primary={solution}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            ))}
          </List>
        </Box>

        {/* Expandable details */}
        <Collapse in={expanded}>
          <Stack spacing={2}>
            {error.details && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Error Details:
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'grey.50' }}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {error.details}
                  </Typography>
                </Paper>
              </Box>
            )}

            {error.context && Object.keys(error.context).length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Context:
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'grey.50' }}>
                  <Typography 
                    variant="body2" 
                    component="pre" 
                    sx={{ 
                      fontFamily: 'monospace',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      fontSize: '0.75rem'
                    }}
                  >
                    {JSON.stringify(error.context, null, 2)}
                  </Typography>
                </Paper>
              </Box>
            )}
          </Stack>
        </Collapse>

        {/* Action buttons */}
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          {displayInfo.actionUrl && (
            <Button
              variant="text"
              startIcon={<HelpIcon />}
              onClick={handleHelpClick}
              size="small"
            >
              Get Help
            </Button>
          )}
          {onDismiss && (
            <Button
              variant="outlined"
              onClick={onDismiss}
              size="small"
            >
              Dismiss
            </Button>
          )}
          {error.retryable && onRetry && (
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={handleRetry}
              size="small"
              disabled={!!error.retryAfter}
            >
              {error.retryAfter ? 
                `Retry in ${Math.ceil(error.retryAfter / 60)}m` : 
                'Try Again'
              }
            </Button>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
};

export default FinancialErrorDisplay;